import { Button } from '@/components/common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '@/components/common/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/common/tabs'
import { useLayoutContext } from '@/context/layout'
import { cn } from '@/utils/cn'
import { type ReactNode, useEffect } from 'react'
import { ExternalLinkIcon } from '../icons/externalLink'
import type { CaseStudyLogoComponent } from './caseStudyLogos'
import { GauntletLogo, JumperLogo, KpkLogo, KrakenLogo, MapleLogo, OtusLogo } from './caseStudyLogos'

const caseStudyAssetPath = '/case-studies'

const caseStudies = [
  {
    id: 'kraken',
    name: 'Kraken Wallet',
    Logo: KrakenLogo,
    imageSrc: `${caseStudyAssetPath}/kraken.webp`,
    href: 'https://docs.vaults.fyi/resources/case-studies/kraken-wallet',
    copy: [
      'Kraken Wallet uses vaults.fyi to show users DeFi earning options inside the wallet.',
      'When someone looks at their tokens, Kraken surfaces relevant yield opportunities across Ethereum, Base, Optimism, Arbitrum, and Polygon.',
      'vaults.fyi powers the market data and portfolio tracking behind the experience, so Kraken does not have to maintain its own protocol-by-protocol yield infrastructure.',
    ],
  },
  {
    id: 'jumper',
    name: 'Jumper',
    Logo: JumperLogo,
    imageSrc: `${caseStudyAssetPath}/jumper.webp`,
    href: 'https://docs.vaults.fyi/resources/case-studies/jumper-earn',
    copy: [
      'Jumper uses vaults.fyi to bring yield discovery into a bridge-first crypto experience.',
      'Users can compare opportunities and move into earning positions without leaving the app flow they already use for cross-chain actions.',
      'vaults.fyi supplies normalized market data, ready-to-sign transaction payloads, and position context across supported networks.',
    ],
  },
  {
    id: 'otus',
    name: 'Otus',
    Logo: OtusLogo,
    imageSrc: `${caseStudyAssetPath}/otus.webp`,
    href: 'https://blog.vaults.fyi/otus-an-ai-yield-account-powered-by-vaults-fyi/',
    copy: [
      'Otus uses vaults.fyi to power a smart yield account for stablecoin users.',
      'The product can surface curated opportunities, prepare deposit transactions, and keep users informed after funds move onchain.',
      'vaults.fyi handles the yield data and portfolio layer so the app can focus on the account experience.',
    ],
  },
  {
    id: 'gauntlet',
    name: 'Gauntlet',
    Logo: GauntletLogo,
    imageSrc: `${caseStudyAssetPath}/gauntlet.webp`,
    href: 'https://docs.vaults.fyi/resources/case-studies/gauntlet',
    copy: [
      'Gauntlet uses vaults.fyi for reliable vault data across the markets they monitor and curate.',
      'Teams can compare rates, liquidity, assets, protocols, curators, and historical behavior through one standardized data layer.',
      'vaults.fyi indexes directly from blockchain data and normalizes yield methodology across protocols and networks.',
    ],
  },
  {
    id: 'kpk',
    name: 'KPK',
    Logo: KpkLogo,
    imageSrc: `${caseStudyAssetPath}/kpk.webp`,
    href: 'https://docs.vaults.fyi/resources/case-studies/kpk',
    copy: [
      'KPK uses vaults.fyi to make ecosystem vaults easier for users and partners to discover.',
      'The integration layer standardizes vault discovery, transaction routing, attribution, and portfolio context across supported markets.',
      'vaults.fyi gives partners ready-to-use data and action payloads without bespoke protocol work for every surface.',
    ],
  },
  {
    id: 'maple',
    name: 'Maple Finance',
    Logo: MapleLogo,
    imageSrc: `${caseStudyAssetPath}/maple.webp`,
    href: 'https://docs.vaults.fyi/resources/case-studies/maple-finance',
    copy: [
      'Maple uses vaults.fyi as an independent data source for yield performance reporting.',
      'Using a third-party data source gives investors a clearer view into performance without asking Maple to grade its own work.',
      'vaults.fyi provides standardized market data that can support reporting, dashboards, and allocator workflows.',
    ],
  },
] as const satisfies readonly {
  id: string
  name: string
  Logo: CaseStudyLogoComponent
  imageSrc: string
  href: string
  copy: readonly string[]
}[]

interface CaseStudiesDialogProps {
  trigger: ReactNode
}

export const CaseStudiesDialog = ({ trigger }: CaseStudiesDialogProps) => <CaseStudiesDialogContent trigger={trigger} />

const CaseStudiesDialogContent = ({ trigger }: CaseStudiesDialogProps) => {
  const { setNavbarExternalFocusPreviewed } = useLayoutContext()

  useEffect(() => () => setNavbarExternalFocusPreviewed(false), [setNavbarExternalFocusPreviewed])

  return (
    <Dialog onOpenChange={setNavbarExternalFocusPreviewed}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        hideCloseButton={false}
        className={cn(
          'w-[calc(100vw-1rem)] max-w-[1000px] rounded-[24px] bg-page p-0 md:w-[calc(100vw-3rem)] md:rounded-[40px]',
          'max-h-[calc(100dvh-1rem)] md:max-h-[calc(100dvh-3rem)]'
        )}
      >
        <DialogBody>
          <Tabs defaultValue={caseStudies[0].id} className="flex min-h-0 flex-1 flex-col">
            <DialogScrollArea className="min-h-0 flex-1">
              <div className="flex flex-col gap-5 px-5 py-6 md:gap-8 md:p-8 md:pt-10">
                <header className="flex flex-col gap-0.5 pr-9 md:gap-0">
                  <DialogTitle className="text-2xl md:text-3xl">See how top crypto teams use our API</DialogTitle>
                  <DialogDescription className="text-body-foreground text-sm md:text-base">
                    Analytics, ready-to-sign transactions, and portfolio tracking. Here&apos;s what teams ship with it.
                  </DialogDescription>
                </header>

                <TabsList
                  variant="line"
                  className="flex w-full max-w-full overflow-x-auto rounded-none border-b-0 bg-transparent p-1"
                >
                  {caseStudies.map((caseStudy) => {
                    const Logo = caseStudy.Logo

                    return (
                      <TabsTrigger
                        key={caseStudy.id}
                        value={caseStudy.id}
                        variant="line"
                        className="group w-auto flex-none px-4 py-3 data-[state=active]:border-pink-500 data-[state=active]:text-heading-foreground"
                        aria-label={caseStudy.name}
                      >
                        <Logo
                          aria-hidden
                          className="h-[18px] w-auto text-heading-foreground transition-colors group-hocus:text-accent [&_path]:transition-colors group-hocus:[&_path]:fill-accent"
                        />
                      </TabsTrigger>
                    )
                  })}
                </TabsList>

                {caseStudies.map((caseStudy) => (
                  <TabsContent key={caseStudy.id} value={caseStudy.id}>
                    <div className="flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1fr)_28rem]">
                      <CaseStudyImage caseStudy={caseStudy} className="md:order-2" />
                      <CaseStudyActions className="md:hidden" />
                      <CaseStudyTextCard caseStudy={caseStudy} className="md:order-1" />
                    </div>
                  </TabsContent>
                ))}
              </div>
            </DialogScrollArea>

            <CaseStudyActions className="hidden px-8 pt-0 pb-8 md:flex md:justify-end" />
          </Tabs>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

interface CaseStudySectionProps {
  caseStudy: (typeof caseStudies)[number]
  className?: string
}

const CaseStudyImage = ({ caseStudy, className }: CaseStudySectionProps) => (
  <div className={cn('overflow-hidden rounded-[18px] bg-surface md:rounded-3xl', className)}>
    <img
      src={caseStudy.imageSrc}
      alt={`${caseStudy.name} case study preview`}
      className="aspect-[458/382] h-full w-full object-cover"
    />
  </div>
)

const CaseStudyActions = ({ className }: { className?: string }) => (
  <div className={cn('flex gap-3', className)}>
    <Button variant="secondary" size="lg" className="flex-1 md:flex-none" asChild>
      <a href="https://docs.vaults.fyi" target="_blank" rel="noopener noreferrer">
        Read the docs
      </a>
    </Button>
    <Button variant="primary" size="lg" className="flex-1 md:flex-none" asChild>
      <a href="https://www.vaults.fyi/api" target="_blank" rel="noopener noreferrer">
        Get API Key
      </a>
    </Button>
  </div>
)

const CaseStudyTextCard = ({ caseStudy, className }: CaseStudySectionProps) => (
  <article
    className={cn(
      'flex flex-col gap-4 rounded-3xl border bg-surface p-5 md:min-h-[382px] md:p-6',
      'text-body-foreground',
      className
    )}
  >
    <div className="flex items-center justify-between gap-3">
      <h3 className="min-w-0 truncate font-medium text-heading-foreground text-lg">{caseStudy.name}</h3>
      <a
        href={caseStudy.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex shrink-0 items-center gap-1 rounded-lg font-medium hocus:text-accent-hover text-accent text-sm outline-none transition-colors focus-visible:ring md:hidden"
      >
        Full case study
        <ExternalLinkIcon />
      </a>
    </div>

    <div className="flex flex-1 flex-col gap-4 font-medium text-sm md:gap-5">
      {caseStudy.copy.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </div>

    <a
      href={caseStudy.href}
      target="_blank"
      rel="noopener noreferrer"
      className="ml-auto hidden items-center gap-1 rounded-lg font-medium hocus:text-accent-hover text-accent text-sm outline-none transition-colors focus-visible:ring md:flex"
    >
      Full case study
      <ExternalLinkIcon />
    </a>
  </article>
)
